import { useState, useEffect } from "react";
import axios from "axios";
import { X, Copy, Check, Trash2, Link2, EyeOff, Eye, Calendar, Clock, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPIRY_OPTIONS = [
  { value: 0, label: "Never expires" },
  { value: 60 * 60, label: "1 hour" },
  { value: 60 * 60 * 24, label: "24 hours" },
  { value: 60 * 60 * 24 * 7, label: "7 days" },
  { value: 60 * 60 * 24 * 30, label: "30 days" },
  { value: 60 * 60 * 24 * 90, label: "90 days" },
];

function buildShareUrl(token) {
  return `${window.location.origin}/share/lead/${token}`;
}

function formatExpiry(iso) {
  if (!iso) return "Never expires";
  try {
    const dt = new Date(iso);
    const diffMs = dt - new Date();
    if (diffMs <= 0) return "Expired";
    const days = Math.floor(diffMs / 86400000);
    const hours = Math.floor((diffMs % 86400000) / 3600000);
    if (days > 0) return `Expires in ${days}d ${hours}h · ${dt.toLocaleString()}`;
    if (hours > 0) return `Expires in ${hours}h · ${dt.toLocaleString()}`;
    const mins = Math.max(1, Math.floor((diffMs % 3600000) / 60000));
    return `Expires in ${mins}m · ${dt.toLocaleString()}`;
  } catch {
    return iso;
  }
}

export default function ShareLinkModal({ leadId, leadName, onClose }) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expiry, setExpiry] = useState(60 * 60 * 24 * 7);
  const [hideContact, setHideContact] = useState(false);
  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [error, setError] = useState(null);

  const token = localStorage.getItem("adminToken");
  const headers = { Authorization: `Bearer ${token}` };

  const loadShares = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/leads/${leadId}/shares`, { headers });
      setShares(res.data || []);
    } catch {
      setError("Could not load existing share links.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadShares(); /* eslint-disable-next-line */ }, [leadId]);

  const createShare = async () => {
    setCreating(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API}/admin/leads/${leadId}/shares`,
        {
          expires_in_seconds: expiry || null,
          hide_contact: hideContact,
          label: label.trim() || null,
        },
        { headers }
      );
      const newShare = res.data;
      setShares((p) => [newShare, ...p]);
      setLabel("");
      // Auto-copy the new link
      try {
        await navigator.clipboard.writeText(buildShareUrl(newShare.token));
        setCopiedId(newShare._id);
        setTimeout(() => setCopiedId((c) => (c === newShare._id ? null : c)), 2200);
      } catch { /* clipboard may be blocked in iframe */ }
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to create share link.");
    } finally {
      setCreating(false);
    }
  };

  const revokeShare = async (shareId) => {
    if (!window.confirm("Revoke this share link? Anyone using it will lose access immediately.")) return;
    try {
      await axios.delete(`${API}/admin/shares/${shareId}`, { headers });
      setShares((p) => p.map((s) => (s._id === shareId ? { ...s, revoked: true } : s)));
    } catch {
      setError("Failed to revoke share link.");
    }
  };

  const copyLink = async (share) => {
    const url = buildShareUrl(share.token);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(share._id);
      setTimeout(() => setCopiedId((c) => (c === share._id ? null : c)), 2200);
    } catch {
      window.prompt("Copy the share link:", url);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" data-testid="share-modal-overlay">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" data-testid="share-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/40">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
              <Link2 className="w-4 h-4 text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold truncate">Share this lead</h2>
              <p className="text-xs text-muted-foreground truncate">Read-only public link · {leadName || "Lead"}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-secondary/40 text-muted-foreground hover:text-foreground transition-colors" data-testid="share-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-5 space-y-5">
          {/* Create new link */}
          <div className="rounded-xl border border-border/40 bg-background/30 p-4 space-y-4">
            <div>
              <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">Create a new link</p>
              <p className="text-xs text-muted-foreground">Anyone with this link can view the lead in a read-only popup. No admin access.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1.5 flex items-center gap-1.5">
                  <Clock className="w-3 h-3" /> Expires
                </label>
                <select
                  value={expiry}
                  onChange={(e) => setExpiry(Number(e.target.value))}
                  className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="share-expiry-select"
                >
                  {EXPIRY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1.5">Label (optional)</label>
                <input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. Sent to Jane Doe"
                  className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="share-label-input"
                />
              </div>
            </div>

            <label className="flex items-start gap-3 p-3 rounded-lg border border-border/40 bg-card/40 cursor-pointer hover:border-primary/40 transition-colors" data-testid="share-hide-contact-row">
              <input
                type="checkbox"
                checked={hideContact}
                onChange={(e) => setHideContact(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-primary cursor-pointer"
                data-testid="share-hide-contact-checkbox"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  {hideContact ? <EyeOff className="w-3.5 h-3.5 text-amber-400" /> : <Eye className="w-3.5 h-3.5 text-emerald-400" />}
                  <span className="text-sm font-medium">Hide contact info</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${hideContact ? "bg-amber-500/15 text-amber-400 border border-amber-500/30" : "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"}`}>
                    {hideContact ? "REDACTED" : "FULL"}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {hideContact
                    ? "Name, phone & email are removed server-side and replaced with a privacy curtain. Phone/email patterns are also stripped from the transcript & incident details — they cannot be recovered via the browser."
                    : "Full lead is visible — name, phone, email and the full transcript."}
                </p>
              </div>
            </label>

            <button
              onClick={createShare}
              disabled={creating}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition disabled:opacity-50"
              data-testid="share-create-btn"
            >
              <Link2 className="w-4 h-4" />
              {creating ? "Generating link…" : "Generate share link"}
            </button>

            {error && <p className="text-xs text-destructive" data-testid="share-error">{error}</p>}
          </div>

          {/* Existing links */}
          <div>
            <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">
              Existing links {shares.length > 0 && <span className="text-muted-foreground font-normal normal-case">· {shares.length}</span>}
            </p>
            {loading ? (
              <p className="text-xs text-muted-foreground">Loading…</p>
            ) : shares.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">No links yet. Create one above.</p>
            ) : (
              <div className="space-y-2" data-testid="shares-list">
                {shares.map((s) => {
                  const url = buildShareUrl(s.token);
                  const isExpired = s.expires_at && new Date(s.expires_at) < new Date();
                  const isInactive = s.revoked || isExpired;
                  return (
                    <div
                      key={s._id}
                      className={`p-3 rounded-lg border ${isInactive ? "border-border/30 bg-muted/10 opacity-60" : "border-border/40 bg-card/30"}`}
                      data-testid={`share-row-${s._id}`}
                    >
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${
                          s.revoked ? "bg-destructive/15 text-destructive border-destructive/30"
                          : isExpired ? "bg-muted text-muted-foreground border-border"
                          : "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                        }`}>
                          {s.revoked ? "REVOKED" : isExpired ? "EXPIRED" : "ACTIVE"}
                        </span>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${s.hide_contact ? "bg-amber-500/15 text-amber-400 border-amber-500/30" : "bg-sky-500/10 text-sky-400 border-sky-500/20"}`}>
                          {s.hide_contact ? "CONTACT HIDDEN" : "FULL ACCESS"}
                        </span>
                        {s.label && <span className="text-xs font-medium text-foreground/90">{s.label}</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          readOnly
                          value={url}
                          className="flex-1 min-w-0 px-2.5 py-1.5 rounded bg-background border border-border/40 text-xs font-mono text-muted-foreground truncate"
                          onClick={(e) => e.target.select()}
                          data-testid={`share-url-${s._id}`}
                        />
                        {!isInactive && (
                          <>
                            <button
                              onClick={() => copyLink(s)}
                              title="Copy link"
                              className="flex-shrink-0 p-1.5 rounded-lg bg-primary/15 text-primary hover:bg-primary/25 transition"
                              data-testid={`share-copy-${s._id}`}
                            >
                              {copiedId === s._id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                            </button>
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Open preview"
                              className="flex-shrink-0 p-1.5 rounded-lg bg-secondary/40 text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition"
                              data-testid={`share-open-${s._id}`}
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          </>
                        )}
                        {!s.revoked && (
                          <button
                            onClick={() => revokeShare(s._id)}
                            title="Revoke"
                            className="flex-shrink-0 p-1.5 rounded-lg bg-destructive/10 text-destructive hover:bg-destructive/20 transition"
                            data-testid={`share-revoke-${s._id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1.5 text-[11px] text-muted-foreground flex-wrap">
                        <Calendar className="w-3 h-3" />
                        <span>{formatExpiry(s.expires_at)}</span>
                        <span className="text-muted-foreground/40">·</span>
                        <span>Created {new Date(s.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
